import { createColumnHelper } from "@tanstack/react-table";
import PlayerCell from "./PlayerCell";
import { PositionFilter, type PlayerDatum } from "./util";

const columnHelper = createColumnHelper<PlayerDatum>();

const PassingGrouping = columnHelper.group({
  header: "Passing",
  columns: [
    columnHelper.accessor("passingAttempts", {
      header: "ATTS",
    }),
    columnHelper.accessor("passingCompletions", {
      header: "COMP",
    }),
    columnHelper.accessor("passingCompletionPercentage", {
      header: "COMP%",
    }),
    columnHelper.accessor("passingYards", {
      header: "YDS",
    }),
    columnHelper.accessor("passingTouchdowns", {
      header: "TDS",
    }),
    columnHelper.accessor("interceptionsThrown", {
      header: "INTS",
    }),
    columnHelper.accessor("sacks", {
      header: "Sacks",
    }),
  ],
});

const RushingGrouping = columnHelper.group({
  header: "Rushing",
  columns: [
    columnHelper.accessor("rushingAttempts", {
      header: "ATTS",
    }),
    columnHelper.accessor("rushingYards", {
      header: "YDS",
    }),
    columnHelper.accessor("rushingYardsPerAttempt", {
      header: "YPA",
    }),
    columnHelper.accessor("rushingTouchdowns", {
      header: "TDS",
    }),
    columnHelper.accessor("fumblesLost", {
      header: "Total Fumbles",
    }),
  ],
});

const ReceivingGrouping = columnHelper.group({
  header: "Receiving",
  columns: [
    columnHelper.accessor("receivingYards", {
      header: "YDS",
    }),
    columnHelper.accessor("receivingReceptions", {
      header: "REC",
    }),
    columnHelper.accessor("receivingTargets", {
      header: "TGTS",
    }),
    columnHelper.accessor("receivingYardsPerReception", {
      header: "YPC",
    }),
    columnHelper.accessor("receivingTouchdowns", {
      header: "TDS",
    }),
  ],
});

const FieldGoalsGrouping = [
  columnHelper.group({
    header: "Total",
    columns: [
      columnHelper.accessor("fieldGoals.total.attempted", {
        header: "FGA",
      }),
      columnHelper.accessor("fieldGoals.total.made", {
        header: "FGM",
      }),
    ],
  }),
  columnHelper.group({
    header: "Under 40 YDS",
    columns: [
      columnHelper.accessor("fieldGoals.zeroToThirtyNineYards.attempted", {
        header: "FGA",
      }),
      columnHelper.accessor("fieldGoals.zeroToThirtyNineYards.made", {
        header: "FGM",
      }),
    ],
  }),
  columnHelper.group({
    header: "40-49 YDS",
    columns: [
      columnHelper.accessor("fieldGoals.fortyToFortyNineYards.attempted", {
        header: "FGA",
      }),
      columnHelper.accessor("fieldGoals.fortyToFortyNineYards.made", {
        header: "FGM",
      }),
    ],
  }),
  columnHelper.group({
    header: "50+ YDS",
    columns: [
      columnHelper.accessor("fieldGoals.fiftyPlusYards.attempted", {
        header: "FGA",
      }),
      columnHelper.accessor("fieldGoals.fiftyPlusYards.made", {
        header: "FGM",
      }),
    ],
  }),
];

const ExtraPointsGrouping = [
  columnHelper.accessor("extraPointsAttempted", {
    header: "XPA",
  }),
  columnHelper.accessor("extraPointsMade", {
    header: "XPM",
  }),
];

const DefenseGrouping = columnHelper.group({
  header: "Defense",
  columns: [
    columnHelper.accessor("pointsAllowed", {
      header: "PA",
    }),
    columnHelper.accessor("passingYardsAllowed", {
      header: "PYA",
    }),
    columnHelper.accessor("rushingYardsAllowed", {
      header: "RYA",
    }),
    columnHelper.accessor("sacks", {
      header: "Sacks",
    }),
    columnHelper.accessor("interceptionsCaught", {
      header: "INTS",
    }),
    columnHelper.accessor("fumblesForced", {
      header: "Fumbles Forced",
    }),
    columnHelper.accessor("fumblesRecovered", {
      header: "Fumbles Recovered",
    }),
  ],
});

const PlayerColumn = columnHelper.accessor("name", {
  header: "Player Name",
  cell: (props) => {
    const {
      name,
      numberFirePlayerId,
      position,
      jerseyNumber,
      teamShortCode,
      teamLogoSrc,
      jerseyLogoSrc,
    } = props.row.original;
    return (
      <PlayerCell
        name={name}
        numberFirePlayerId={numberFirePlayerId ?? 0}
        position={position}
        jerseyNumber={jerseyNumber ?? ""}
        teamShortCode={teamShortCode}
        teamLogoSrc={teamLogoSrc}
        jerseyLogoSrc={jerseyLogoSrc}
      />
    );
  },
});

export const TABLE_COLUMN_DEFINITIONS_MAP = {
  [PositionFilter.QUARTERBACK]: [
    PlayerColumn,
    PassingGrouping,
    RushingGrouping,
  ],
  [PositionFilter.RUNNING_BACK]: [
    PlayerColumn,
    RushingGrouping,
    ReceivingGrouping,
  ],
  [PositionFilter.RECEIVER]: [PlayerColumn, ReceivingGrouping, RushingGrouping],
  [PositionFilter.TIGHT_END]: [
    PlayerColumn,
    ReceivingGrouping,
    RushingGrouping,
  ],
  [PositionFilter.FLEX]: [PlayerColumn, RushingGrouping, ReceivingGrouping],
  [PositionFilter.KICKER]: [
    PlayerColumn,
    columnHelper.group({
      header: "Extra Points",
      columns: ExtraPointsGrouping,
    }),
    ...FieldGoalsGrouping,
  ],
  [PositionFilter.DEFENSE]: [PlayerColumn, DefenseGrouping],
};
